// React
import { useEffect, useState } from "react"

// Third-Party Libraries
import axios from "axios"

// Types
import type { SuccessFetch } from "types"
import type { DataBillType, DetailBillType, PaymentBillType, PoFetchType } from "../../types"

// Utils
import { useApi } from "utils"
import { default_option } from "pages/Purchase/utils"
import { billStatusList } from "../vars"
import { useConvertionData } from "pages/Purchase/Transaction/utils/hooks/useConvertionData"

export function useBillDetail(
  id: string,
  bill_id: string
) {
  // Hooks
  const api = useApi()
  const [data, setData] = useState<DetailBillType | null>(null)
  const [billData, setBillData] = useState<DataBillType | null>(null)
  const [isLoading, setLoading] = useState<boolean>(true)
  const {getData} = useConvertionData()

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        const [respo, res] = await axios.all([
          api.get("/probill/detailbill_bypo", {
            params: { purchase_order_id: id }
          }),
          api.get("/probill/detailbill", {
            params: { bill_id }
          })
        ])
  
        const datapo: SuccessFetch<{ payload: PoFetchType }> = respo
        const data: SuccessFetch<{
          payload: {
            databill: any
            detailbill: any[]
            paymentbill: PaymentBillType[]
            attachment?: Array<{ file_path: string }>
          }
        }> = res
  
        const payloadpo = datapo.data.payload
        const payload = data.data.payload
  
        //Ambil semua quantity_unit secara paralel dari API getData()
        const convertedUnits = await Promise.all(
          payload.detailbill.map(item => getData(item.product_id.toString()))
        )
  
        //Gabungkan hasil getData dengan detailbill
        const detailbillWithConversion = payload.detailbill.map((item, index) => ({
          ...item,
          quantity_unit: convertedUnits[index] ?? item.quantity_unit
        }))
  
        setBillData({
          approve_status: payload.databill.approve_status,
          approval_status: default_option.find(opt => opt.value === String(payload.databill.approve_status))!.label,
          approved_by: payload.databill.approve_by_name,
          created_by: payload.databill.created_by ?? NaN,
          bill_status: billStatusList[`${payload.databill.bill_status ?? ""}`],
          po_code: payload.databill.po_code,
          total_payment: payload.databill.total_payment,
          payment_list: payload.paymentbill,
          attachment: payload.attachment
        })
  
        setData({
          datapo: {
            address: payload.databill.address,
            company_code: payload.databill.company_code,
            delivered: payload.databill.delivery_no,
            freight: payload.databill.freight,
            location_id: payload.databill.location_id.toString(),
            location_name: payload.databill.location_name,
            memo: payload.databill.memo,
            payment_term: payloadpo.datapo.payment_term.toString(),
            po_delivery_status: payloadpo.datapo.po_delivery_status,
            po_payment_status: payloadpo.datapo.po_payment_status,
            po_status: payloadpo.datapo.po_status,
            promize_date: payload.databill.promise_date,
            purchase_order_id: payload.databill.purchase_order_id.toString(),
            referensi: payload.databill.referensi,
            tax_inclusive: payloadpo.datapo.tax_inclusive,
            transaction_date: payload.databill.transaction_date,
            trx_code: payload.databill.trx_code,
            vendor_code: payloadpo.datapo.vendor_code,
            vendor_id: payload.databill.vendor_id.toString(),
            vendor_name: payload.databill.vendor_name
          },
          detailbill: payloadpo.detailbill.map(item => ({
            ...item,
            delivered: parseInt(item.delivered),
            discount: parseInt(item.discount),
            mr_unit_id: item.mr_unit_id.toString(),
            product_id: item.product_id.toString(),
            project_id: item.project_id.toString(),
            project_name: item.project_name,
            product_foto: item.product_foto,
            tax_id: item.tax_id.toString()
          })),
          reminder: null,
          freight: payload.databill.freight,
          trx_code: payload.databill.trx_code,
          probildetail: detailbillWithConversion.map((item, idx) => {
            const podetail = payloadpo.detailbill.find(i => i.product_id === item.product_id)
            const qtyUnit = convertedUnits[idx] ?? item.quantity_unit

            return {
              bill_detail_id: item.bill_detail_id,
              mr_unit_id: podetail?.mr_unit_id.toString() ?? "",
              project_id: item?.project_id.toString() ?? "",
              project_name: item?.project_name ?? "",
              qty_multiply: qtyUnit,
              qty_order: podetail ? (podetail.qty_order - parseInt(podetail.delivered ?? 0)) : 0,
              discount: item.discount,
              price_bill: item.price_bill,
              product_id: item.product_id.toString(),
              quantity_bill: item.quantity_bill,
              quantity_unit: qtyUnit * item.quantity_bill,
              tax_id: item.tax_id.toString(),
              total: item.total,
              location_warehouse_id: item.location_warehouse_id ? item.location_warehouse_id.toString() : null,
              inventory: podetail?.inventory ?? item.inventory ?? null
            }
          })
        })
      } catch (err) {
        setData(null)
      } finally {
        setLoading(false)
      }
    }
  
    fetchData()
  
    return () => {
      setData(null)
      setLoading(true)
    }
  
    // eslint-disable-next-line
  }, [])

  return { billData, data, isLoading }
}


export function useGetDetailBill( bill_id: string ) {
  const api = useApi()
  const [data, setData] = useState<DetailBillType | null>(null)
  const [isLoading, setLoading] = useState<boolean>(true)

  useEffect(() => {
    axios.all([api.get("/probill/detailbill", { params: { bill_id } })]).then(axios.spread((res) => {
      const data: SuccessFetch<{ payload: DetailBillType }> = res
      const payload = data.data.payload
      
      setData(payload)
    }))
    .catch(() => setData(null))
    .finally(() => setLoading(false))

    return () => {
      setData(null)
      setLoading(true)
    }

    // eslint-disable-next-line
  }, [])

  return { data, isLoading }
}